import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", key: "instagram", fallback: "Instagram" },
  { value: "tiktok", key: "tiktok", fallback: "TikTok" },
  { value: "youtube", key: "youtube", fallback: "YouTube" },
  { value: "x", key: "x", fallback: "X / Twitter" },
  { value: "facebook", key: "facebook", fallback: "Facebook" },
  { value: "linkedin", key: "linkedin", fallback: "LinkedIn" },
  { value: "general", key: "general", fallback: "General" },
];

const scoreColorByValue = (score) => {
  const safeScore = Number(score) || 0;
  if (safeScore >= 80) return "success";
  if (safeScore >= 60) return "warning";
  return "danger";
};

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return item.label || item.title || item.name || item.text || JSON.stringify(item);
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((item) => item.replace(/^[-*•\d.\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const toPrettyLabel = (value) => {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatResponseToText = (result, t) => {
  if (!result) return "";

  const sections = [];

  const overallScore =
    result.overall_score ??
    result.score ??
    result.hook_score ??
    result.total_score ??
    result.analysis?.overall_score;

  if (overallScore !== undefined && overallScore !== null && overallScore !== "") {
    sections.push(
      `${t("studioTools.hookScoreAnalyzerTool.exportLabels.overallScore")}: ${overallScore}/100`
    );
  }

  const verdict =
    result.verdict ||
    result.summary ||
    result.overview ||
    result.analysis?.verdict ||
    result.analysis?.summary;

  if (verdict) {
    sections.push(`${t("studioTools.hookScoreAnalyzerTool.exportLabels.summary")}:\n${verdict}`);
  }

  const breakdown =
    result.breakdown ||
    result.score_breakdown ||
    result.analysis?.breakdown ||
    result.analysis?.score_breakdown;

  if (breakdown && typeof breakdown === "object" && !Array.isArray(breakdown)) {
    const lines = Object.entries(breakdown).map(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        const score = value.score ?? value.value ?? value.rating ?? "";
        const note = value.reason || value.note || value.feedback || value.description || "";
        return `${toPrettyLabel(key)}: ${score}${note ? ` — ${note}` : ""}`;
      }
      return `${toPrettyLabel(key)}: ${value}`;
    });

    if (lines.length) {
      sections.push(
        `${t("studioTools.hookScoreAnalyzerTool.exportLabels.breakdown")}:\n${lines
          .map((line) => `- ${line}`)
          .join("\n")}`
      );
    }
  }

  const strengths = normalizeArray(
    result.strengths || result.what_works || result.analysis?.strengths
  );
  if (strengths.length) {
    sections.push(
      `${t("studioTools.hookScoreAnalyzerTool.exportLabels.strengths")}:\n${strengths
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const weaknesses = normalizeArray(
    result.weaknesses || result.gaps || result.issues || result.analysis?.weaknesses
  );
  if (weaknesses.length) {
    sections.push(
      `${t("studioTools.hookScoreAnalyzerTool.exportLabels.weaknesses")}:\n${weaknesses
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const suggestions = normalizeArray(
    result.suggestions ||
      result.recommendations ||
      result.improvements ||
      result.analysis?.suggestions
  );
  if (suggestions.length) {
    sections.push(
      `${t("studioTools.hookScoreAnalyzerTool.exportLabels.suggestions")}:\n${suggestions
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const improvedHooks = normalizeArray(
    result.improved_hooks ||
      result.better_versions ||
      result.rewrites ||
      result.alternative_hooks ||
      result.analysis?.improved_hooks
  );
  if (improvedHooks.length) {
    sections.push(
      `${t("studioTools.hookScoreAnalyzerTool.exportLabels.improvedHooks")}:\n${improvedHooks
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  if (!sections.length) {
    return typeof result === "string" ? result : JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const renderScorePillStyles = (theme, variant) => {
  const base = {
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid transparent",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };

  const lightVariants = {
    success: {
      background: "rgba(16, 185, 129, 0.12)",
      color: "#047857",
      borderColor: "rgba(16, 185, 129, 0.22)",
    },
    warning: {
      background: "rgba(245, 158, 11, 0.12)",
      color: "#b45309",
      borderColor: "rgba(245, 158, 11, 0.22)",
    },
    danger: {
      background: "rgba(239, 68, 68, 0.12)",
      color: "#b91c1c",
      borderColor: "rgba(239, 68, 68, 0.22)",
    },
  };

  const darkVariants = {
    success: {
      background: "rgba(16, 185, 129, 0.16)",
      color: "#6ee7b7",
      borderColor: "rgba(16, 185, 129, 0.25)",
    },
    warning: {
      background: "rgba(245, 158, 11, 0.16)",
      color: "#fcd34d",
      borderColor: "rgba(245, 158, 11, 0.25)",
    },
    danger: {
      background: "rgba(239, 68, 68, 0.16)",
      color: "#fca5a5",
      borderColor: "rgba(239, 68, 68, 0.25)",
    },
  };

  const palette = theme === "dark" ? darkVariants : lightVariants;

  return {
    ...base,
    ...(palette[variant] || palette.warning),
  };
};

const HookScoreAnalyzerTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [hook, setHook] = useState("");
  const [goal, setGoal] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.88) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 760,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(99,102,241,0.16)" : "rgba(79,70,229,0.09)",
        color: isDark ? "#c7d2fe" : "#4338ca",
        border: `1px solid ${isDark ? "rgba(99,102,241,0.24)" : "rgba(79,70,229,0.14)"}`,
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        transition: "all 0.2s ease",
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 140,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "14px",
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: loading
          ? isDark
            ? "rgba(99,102,241,0.45)"
            : "rgba(79,70,229,0.45)"
          : isDark
          ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
          : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
        color: "#ffffff",
        boxShadow: loading
          ? "none"
          : isDark
          ? "0 12px 24px rgba(99,102,241,0.22)"
          : "0 12px 24px rgba(79,70,229,0.18)",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(30,41,59,0.82)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.55)" : "rgba(248,250,252,0.95)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.7,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      scoreWrap: {
        display: "flex",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
      },
      scoreBig: {
        fontSize: 32,
        fontWeight: 900,
        letterSpacing: "-0.03em",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      metricGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      },
      metricCard: {
        borderRadius: 16,
        padding: 14,
        background: isDark ? "rgba(15,23,42,0.82)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      },
      metricLabel: {
        fontSize: 12,
        fontWeight: 700,
        marginBottom: 8,
        color: isDark ? "rgba(226,232,240,0.7)" : "rgba(15,23,42,0.6)",
      },
      metricValue: {
        fontSize: 15,
        fontWeight: 800,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
    };
  }, [isDark, loading]);

  const formattedText = useMemo(() => formatResponseToText(result, t), [result, t]);

  const overallScore =
    result?.overall_score ??
    result?.score ??
    result?.hook_score ??
    result?.total_score ??
    result?.analysis?.overall_score;

  const breakdown =
    result?.breakdown ||
    result?.score_breakdown ||
    result?.analysis?.breakdown ||
    result?.analysis?.score_breakdown ||
    {};

  const strengths = normalizeArray(
    result?.strengths || result?.what_works || result?.analysis?.strengths
  );
  const weaknesses = normalizeArray(
    result?.weaknesses || result?.gaps || result?.issues || result?.analysis?.weaknesses
  );
  const suggestions = normalizeArray(
    result?.suggestions ||
      result?.recommendations ||
      result?.improvements ||
      result?.analysis?.suggestions
  );
  const improvedHooks = normalizeArray(
    result?.improved_hooks ||
      result?.better_versions ||
      result?.rewrites ||
      result?.alternative_hooks ||
      result?.analysis?.improved_hooks
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setHook("");
    setGoal("");
    setTargetAudience("");
    setResult(null);
    setError("");
    setCopied(false);
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (copyError) {
      setError(t("studioTools.hookScoreAnalyzerTool.messages.copyFailed"));
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleAnalyze = async () => {
    if (!hook.trim()) {
      setError(t("studioTools.hookScoreAnalyzerTool.messages.hookRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      hook: hook.trim(),
      topic: topicValue.trim(),
      platform: selectedPlatform,
      goal: goal.trim(),
      target_audience: targetAudience.trim(),
    };

    try {
      const data = await tryPost(
        [
          "/studio/hook-score",
          "/studio/hook_score",
          "/studio/hook-score-analyzer",
          "/studio/hook_score_analyzer",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.analysis ||
        data?.data ||
        data?.hook_score_analysis ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t("studioTools.hookScoreAnalyzerTool.messages.analyzeFailed");
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>{t("studioTools.hookScoreAnalyzerTool.badge")}</span>
            <h2 style={styles.title}>{t("studioTools.hookScoreAnalyzerTool.title")}</h2>
            <p style={styles.subtitle}>{t("studioTools.hookScoreAnalyzerTool.description")}</p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.hookScoreAnalyzerTool.fields.topic.label")}</label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t("studioTools.hookScoreAnalyzerTool.fields.topic.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.hookScoreAnalyzerTool.fields.topic.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.hookScoreAnalyzerTool.fields.platform.label")}</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.hookScoreAnalyzerTool.platformOptions.${option.key}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.hookScoreAnalyzerTool.fields.platform.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.hookScoreAnalyzerTool.fields.goal.label")}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t("studioTools.hookScoreAnalyzerTool.fields.goal.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.hookScoreAnalyzerTool.fields.goal.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.hookScoreAnalyzerTool.fields.targetAudience.label")}</label>
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder={t("studioTools.hookScoreAnalyzerTool.fields.targetAudience.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.hookScoreAnalyzerTool.fields.targetAudience.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>{t("studioTools.hookScoreAnalyzerTool.fields.hook.label")}</label>
            <textarea
              value={hook}
              onChange={(e) => setHook(e.target.value)}
              placeholder={t("studioTools.hookScoreAnalyzerTool.fields.hook.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.hookScoreAnalyzerTool.fields.hook.hint")} />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleAnalyze}
            disabled={loading}
          >
            {loading
              ? t("studioTools.hookScoreAnalyzerTool.actions.analyzing")
              : t("studioTools.hookScoreAnalyzerTool.actions.analyze")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.hookScoreAnalyzerTool.actions.clearResults")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.hookScoreAnalyzerTool.actions.reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>
            {t("studioTools.hookScoreAnalyzerTool.tip")}
          </span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard
            colors={{
              border: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)",
              cardBgStrong: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
              shadowSoft: isDark
                ? "0 10px 24px rgba(2,6,23,0.22)"
                : "0 6px 18px rgba(15,23,42,0.05)",
            }}
            index={0}
          >
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>{t("studioTools.hookScoreAnalyzerTool.results.title")}</h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.hookScoreAnalyzerTool.actions.copied")
                    : t("studioTools.hookScoreAnalyzerTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="hook_score"
                  title={`Hook Score Analysis${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || "Hook Score Analysis"}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    hook,
                    goal,
                    targetAudience,
                    overallScore: overallScore ?? null,
                  }}
                />
              </ActionRow>
            </div>

            {overallScore !== undefined && overallScore !== null ? (
              <div style={styles.scoreWrap}>
                <div style={styles.scoreBig}>{overallScore}/100</div>
                <span
                  style={renderScorePillStyles(
                    theme,
                    scoreColorByValue(overallScore)
                  )}
                >
                  {Number(overallScore) >= 80
                    ? t("studioTools.hookScoreAnalyzerTool.results.scoreLabels.strong")
                    : Number(overallScore) >= 60
                    ? t("studioTools.hookScoreAnalyzerTool.results.scoreLabels.decent")
                    : t("studioTools.hookScoreAnalyzerTool.results.scoreLabels.weak")}
                </span>
              </div>
            ) : null}

            {result?.verdict || result?.summary || result?.overview || result?.analysis?.summary ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.summary")}</h4>
                <p style={styles.paragraph}>
                  {result?.verdict ||
                    result?.summary ||
                    result?.overview ||
                    result?.analysis?.summary}
                </p>
              </div>
            ) : null}

            {breakdown && typeof breakdown === "object" && Object.keys(breakdown).length ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.breakdown")}</h4>
                <div style={styles.metricGrid}>
                  {Object.entries(breakdown).map(([key, value]) => {
                    const score =
                      typeof value === "object" && value !== null
                        ? value.score ?? value.value ?? value.rating ?? "—"
                        : value;
                    const note =
                      typeof value === "object" && value !== null
                        ? value.reason ||
                          value.note ||
                          value.feedback ||
                          value.description ||
                          ""
                        : "";

                    return (
                      <div key={key} style={styles.metricCard}>
                        <div style={styles.metricLabel}>{toPrettyLabel(key)}</div>
                        <div style={styles.metricValue}>{score}</div>
                        {note ? (
                          <p style={{ ...styles.paragraph, marginTop: 8, fontSize: 13 }}>
                            {note}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {strengths.length ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.whatWorks")}</h4>
                <ul style={styles.list}>
                  {strengths.map((item, index) => (
                    <li key={`strength-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {weaknesses.length ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.weakPoints")}</h4>
                <ul style={styles.list}>
                  {weaknesses.map((item, index) => (
                    <li key={`weakness-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {suggestions.length ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.howToImprove")}</h4>
                <ul style={styles.list}>
                  {suggestions.map((item, index) => (
                    <li key={`suggestion-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {improvedHooks.length ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.improvedHooks")}</h4>
                <ul style={styles.list}>
                  {improvedHooks.map((item, index) => (
                    <li key={`improved-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!formattedText ? (
              <div>
                <h4 style={styles.sectionTitle}>{t("studioTools.hookScoreAnalyzerTool.results.rawResult")}</h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default HookScoreAnalyzerTool;